import classNames from "classnames";
import pluralize from "pluralize";
import {MouseEvent} from "react";
import {FiEdit2, FiTrash} from "react-icons/fi";

import {CheckboxInput} from "@/components/checkbox/CheckboxInput/CheckboxInput.tsx";
import {Chip} from "@/components/chip/Chip.tsx";
import {Dropdown} from "@/components/dropdown/Dropdown.tsx";
import {IconButton} from "@/components/iconButton/IconButton.tsx";
import {Tooltip} from "@/components/toolTip/Tooltip.tsx";

import styles from "./ViewSelectorDropdownItem.module.scss";

interface ViewSelectorDropdownItemProps {
  name: string;
  columnCount: number;
  isActive: boolean;
  isDefault?: boolean;
  onSelect: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

export const ViewSelectorDropdownItem = ({
  name,
  columnCount,
  isActive,
  isDefault = false,
  onSelect,
  onEdit,
  onDelete,
}: ViewSelectorDropdownItemProps) => {
  const withStopPropagation = (handler: () => void) => (event: MouseEvent) => {
    event.stopPropagation();
    handler();
  };

  const hasActions = !!(onEdit || onDelete);

  return (
    <Dropdown.Item className={classNames(styles.item, {[styles.hasActions]: hasActions})} onClick={onSelect}>
      <span className={styles.label}>
        <CheckboxInput type="radio" variant="selectionCard" checked={isActive} readOnly tabIndex={-1} />

        <Tooltip onModal tooltipContent={name} delayMs={500}>
          <span className={styles.name}>{name}</span>
        </Tooltip>
        {isDefault && (
          <Chip size="small" variant={"tag"}>
            Default
          </Chip>
        )}
      </span>

      <span className={styles.meta}>
        <span className={styles.columnCount}>
          {columnCount} {pluralize("column", columnCount)}
        </span>
        {hasActions && (
          <span className={styles.actions}>
            {onEdit && (
              <IconButton
                icon={FiEdit2}
                variant="ghost"
                size={"fitIcon"}
                aria-label={`Edit ${name}`}
                onClick={withStopPropagation(onEdit)}
              />
            )}
            {onDelete && (
              <IconButton
                icon={FiTrash}
                variant="ghost"
                size={"fitIcon"}
                className={styles.deleteAction}
                aria-label={`Delete ${name}`}
                onClick={withStopPropagation(onDelete)}
              />
            )}
          </span>
        )}
      </span>
    </Dropdown.Item>
  );
};
